import { sortBy } from '@debbie/common/dist/economy/helpers/utils.js';
import { TenantStatus } from '@debbie/common/dist/tenants/index.js';
import { search } from '@debbie/common/dist/util/index.js';
import { selectAuthToken } from '@debbie/cortex/dist/auth/selectors.js';
import { useAccessCreditors } from '@debbie/cortex/dist/creditors/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { setSelectedCreditorId } from '@debbie/cortex/dist/internal.js';
import { setTenantId, useTenantId } from '@debbie/cortex/dist/tenant/index.js';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { CASEWORKER_URL, DEFAULT_CREDITOR_WEB, IS_PROD } from '../../config.js';
import CreditorOrTenant from '../CreditorOrTenant.js';
import { LogOut } from '../Icons/index.js';
import Input from '../Input.js';
import TenantStatusBadge from '../TenantStatusBadge.js';
const tenantStatusOrder = {
    ['ACTIVE']: 1,
    ['GETTING_STARTED']: 2,
    ['DEMO']: 3,
    ['SANDBOX']: 4,
    ['DISABLED']: 5,
    ['creditor']: 6,
};
const filter = (accessCreditors) => {
    const isSystemStandardDomain = (DEFAULT_CREDITOR_WEB || '').includes(window.location.hostname) ||
        (CASEWORKER_URL || '').includes(window.location.hostname) ||
        !IS_PROD;
    if (isSystemStandardDomain) {
        return accessCreditors;
    }
    else {
        return accessCreditors.filter((creditor) => {
            return creditor.url.includes(window.location.hostname);
        });
    }
};
const SelectableEntities = (props) => {
    const selectedTenantId = useTenantId();
    const selectedCreditorId = useSelector((s) => s.selectedCreditor);
    const lang = useSelector((s) => s.lang);
    const authToken = useSelector(selectAuthToken);
    const accessibleCreditorsLoadable = useAccessCreditors();
    const _accessibleCreditors = h.hasData(accessibleCreditorsLoadable)
        ? accessibleCreditorsLoadable.data
        : null;
    const accessibleCreditors = _accessibleCreditors
        ? filter(_accessibleCreditors)
        : null;
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedIndex, setSelectedIndex] = React.useState(null);
    const entities = props.tenants
        .filter((tenant) => 
    // Only redirect to tenants that the user has "caseworking" access to
    ['super-admin', 'admin', 'caseworker'].includes(tenant.role))
        .map((tenant) => {
        let badge = undefined;
        if ([
            TenantStatus.Disabled,
            TenantStatus.Demo,
            TenantStatus.Sandbox,
            TenantStatus.GettingStarted,
        ].includes(tenant.status)) {
            badge = (React.createElement(TenantStatusBadge, { status: tenant.status, style: { float: 'right' } }));
        }
        return {
            type: 'tenant',
            groupByKey: tenant.status,
            id: tenant.id,
            details: tenant,
            active: tenant.id === selectedTenantId,
            link: `${CASEWORKER_URL}?tenantId=${tenant.id}&authToken=${authToken}`,
            name: tenant.name,
            tooltip: tenant.name +
                (tenant.status !== TenantStatus.Active
                    ? ' [' + lang.tenantStatus[tenant.status] + ']'
                    : ''),
            badge,
            description: (React.createElement(React.Fragment, null,
                React.createElement("b", null, tenant.countryCode),
                " ",
                tenant.name)),
        };
    })
        .concat((accessibleCreditors || []).map((creditor) => ({
        type: 'creditor',
        groupByKey: 'creditor',
        id: creditor.creditorId,
        details: creditor,
        active: creditor.creditorId === selectedCreditorId,
        link: `${creditor.url}/?creditorId=${creditor.creditorId}&tenantId=${creditor.tenantId}&authToken=${authToken}`,
        tooltip: creditor.name,
        name: creditor.name,
        description: React.createElement(React.Fragment, null, creditor.id),
    })))
        .sort(sortBy((a, b) => tenantStatusOrder[a.groupByKey] - tenantStatusOrder[b.groupByKey], (a, b) => a.name.localeCompare(b.name)));
    const filteredEntities = (searchQuery
        ? search(entities, (entity) => entity.name + entity.id, searchQuery, {})
        : entities).map((entity, i) => ({
        ...entity,
        selected: selectedIndex === i,
    }));
    const groupedEntities = filteredEntities.reduce((acc, entity) => {
        if (!acc[entity.groupByKey]) {
            acc[entity.groupByKey] = [];
        }
        acc[entity.groupByKey] = acc[entity.groupByKey].concat(entity);
        return acc;
    }, {});
    const select = (entity) => {
        setSelectedCreditorId(null);
        setTenantId(undefined);
        location.replace(entity.link);
    };
    return (React.createElement(React.Fragment, null,
        props.tenants.length > 2 && (React.createElement(Input, { placeholder: lang.misc.search, value: searchQuery, onChange: (e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
            }, autofocus: true, onFocus: () => setSelectedIndex(0), onKeyDown: (e) => {
                switch (e.key) {
                    case 'ArrowUp': {
                        const newIndex = (selectedIndex || 0) - 1;
                        setSelectedIndex(newIndex < 0 ? filteredEntities.length - 1 : newIndex);
                        break;
                    }
                    case 'ArrowDown': {
                        const newIndex = (selectedIndex || 0) + 1;
                        setSelectedIndex(newIndex >= filteredEntities.length ? 0 : newIndex);
                        break;
                    }
                    case 'Enter': {
                        const selectedEntity = filteredEntities.find((entity) => entity.selected);
                        if (selectedEntity) {
                            select(selectedEntity);
                        }
                        break;
                    }
                }
            } })),
        Object.entries(groupedEntities).map(([type, entities]) => {
            return (React.createElement("div", { key: type },
                props.tenants.length > 3 && (React.createElement("h4", null, type === 'creditor'
                    ? lang.rightNav.creditors
                    : lang.tenantStatusPlural[type])),
                React.createElement("div", null, entities.map((entity) => {
                    return (React.createElement(CreditorOrTenant, { key: entity.id, active: entity.active, onClick: () => select(entity), selected: entity.selected, name: entity.name, description: entity.description, badge: entity.badge }));
                }))));
        }),
        React.createElement("p", { onClick: () => {
                c.auth.logout();
            }, style: {
                marginTop: '1.5em',
                cursor: 'pointer',
                textAlign: 'center',
            } },
            React.createElement(LogOut, { style: { margin: '-0.1em 0' } }),
            " ",
            lang.auth.logout)));
};
export default SelectableEntities;
//# sourceMappingURL=SelectableEntities.js.map